import { Input } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardShortcutKeys from '../../Common/KeyboardShortcutKeys';
import {
  formatKeyboardShortcut,
  getKeyboardShortcutsForHelp,
  type KeyboardShortcutCategory,
} from '../../../lib/keyboardShortcuts';

export interface KeyboardShortcutsFormProps {
  onClose: () => void;
}

const CATEGORY_ORDER: KeyboardShortcutCategory[] = [
  'app',
  'create',
  'file',
  'selection',
  'view',
  'navigation',
];

const CATEGORY_I18N: Record<KeyboardShortcutCategory, string> = {
  app: 'shortcuts.categories.app',
  create: 'shortcuts.categories.create',
  file: 'shortcuts.categories.file',
  navigation: 'shortcuts.categories.navigation',
  selection: 'shortcuts.categories.selection',
  view: 'shortcuts.categories.view',
};

/**
 * Searchable read-only list of keyboard shortcuts grouped by category.
 */
export default function KeyboardShortcutsForm({ onClose }: KeyboardShortcutsFormProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const groupedShortcuts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const entries = getKeyboardShortcutsForHelp().filter((entry) => {
      if (!normalizedQuery) return true;
      const label = t(entry.i18nKey).toLowerCase();
      const binding = formatKeyboardShortcut(
        typeof navigator !== 'undefined' && (navigator.platform || '').toLowerCase().includes('mac')
          ? entry.mac
          : entry.windows,
      ).toLowerCase();
      return label.includes(normalizedQuery) || binding.includes(normalizedQuery);
    });

    return CATEGORY_ORDER.map((category) => ({
      category,
      items: entries.filter((entry) => entry.category === category),
    })).filter((group) => group.items.length > 0);
  }, [query, t]);

  return (
    <div className="flex flex-col gap-4">
      <Input
        allowClear
        aria-label={t('shortcuts.searchLabel')}
        placeholder={t('shortcuts.searchPlaceholder')}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      <div
        className="max-h-[60vh] overflow-y-auto"
        role="region"
        aria-label={t('shortcuts.listLabel')}
      >
        {groupedShortcuts.map((group) => (
          <section key={group.category} className="mb-5">
            <h3 className="text-blue-dark mb-3 text-sm font-semibold tracking-wide uppercase">
              {t(CATEGORY_I18N[group.category])}
            </h3>
            <ul className="divide-gray-medium/60 divide-y">
              {group.items.map((entry) => {
                const binding =
                  typeof navigator !== 'undefined' &&
                  (navigator.platform || '').toLowerCase().includes('mac')
                    ? entry.mac
                    : entry.windows;
                return (
                  <li
                    key={entry.id}
                    className="flex min-h-[3.25rem] items-center justify-between gap-6 py-3.5"
                  >
                    <span className="text-blue-dark text-sm leading-snug">{t(entry.i18nKey)}</span>
                    <KeyboardShortcutKeys binding={binding} className="shrink-0" size="md" />
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {groupedShortcuts.length === 0 && (
          <p className="text-gray-dark text-sm">{t('shortcuts.noResults')}</p>
        )}
      </div>
      <button
        type="button"
        className="text-azure self-end text-sm hover:underline"
        onClick={onClose}
      >
        {t('common.close')}
      </button>
    </div>
  );
}
